// Moving media for prototypes: video fills play, animated GIFs animate. The renderer draws whatever
// frame is current; Present redraws while anything is moving.

interface Gif {
  canvas: HTMLCanvasElement;
  frames: { frame: VideoFrame; ms: number }[];
  total: number;
  shown: number;
}

// WebCodecs' ImageDecoder (Chromium); without it GIFs stay still
type Decoder = { tracks: { ready: Promise<void>; selectedTrack: { frameCount: number } | null }; decode(o: { frameIndex: number }): Promise<{ image: VideoFrame }>; close(): void };
const ImageDecoderCtor = (globalThis as unknown as { ImageDecoder?: new (o: { data: ArrayBuffer; type: string }) => Decoder }).ImageDecoder;

/** How a video layer plays: its settings, and what the prototype has done to it since. */
export interface VideoControl {
  playback(id: string): { autoplay: boolean; loop: boolean; muted: boolean };
  /** Playback the prototype set (play/pause, mute), if any. */
  state(id: string): { playing: boolean; muted: boolean } | undefined;
  /** The video moved from `from` to `to` seconds (for ON_MEDIA_HIT). */
  onTime?(id: string, from: number, to: number): void;
  onEnded?(id: string): void;
  /** A paused video has a new frame to show (first frame loaded, a seek finished). */
  onFrame?(): void;
}

export class MediaPlayer {
  /** keyed by layer and source: each video layer plays on its own */
  private videos = new Map<string, HTMLVideoElement>();
  private layerOf = new Map<HTMLVideoElement, string>();
  private gifs = new Map<string, Gif | 'loading' | 'still'>();
  private start = performance.now();

  /** True while something is playing (Present keeps redrawing). */
  get moving() {
    return [...this.videos.values()].some((v) => !v.paused) || [...this.gifs.values()].some((g) => typeof g === 'object' && g.frames.length > 1);
  }

  constructor(private control?: VideoControl) {}

  get(src: string, nodeId?: string): CanvasImageSource | undefined {
    if (/\.gif($|\?)|^data:image\/gif/i.test(src)) return this.gif(src);
    const key = `${nodeId ?? ''}|${src}`;
    let v = this.videos.get(key);
    if (!v) {
      const video = (v = document.createElement('video'));
      const p = nodeId && this.control ? this.control.playback(nodeId) : { autoplay: true, loop: true, muted: true };
      const st = nodeId ? this.control?.state(nodeId) : undefined;
      // browsers only autoplay muted videos; sound starts with a click (an unmute interaction)
      video.muted = st?.muted ?? p.muted;
      video.loop = p.loop;
      video.playsInline = true;
      video.crossOrigin = 'anonymous';
      // load the first frame even when it doesn't autoplay, so it shows paused
      video.preload = 'auto';
      video.addEventListener('loadeddata', () => this.control?.onFrame?.());
      video.addEventListener('seeked', () => this.control?.onFrame?.());
      video.addEventListener('pause', () => this.control?.onFrame?.());
      video.src = src;
      if (st?.playing ?? p.autoplay) void video.play().catch(() => this.playMuted(video));
      if (nodeId) {
        this.layerOf.set(video, nodeId);
        let last = 0;
        video.addEventListener('timeupdate', () => {
          const now = video.currentTime;
          // a loop restarting counts from zero again
          if (now < last) last = 0;
          if (now > last) this.control?.onTime?.(nodeId, last, now);
          last = now;
        });
        video.addEventListener('ended', () => this.control?.onEnded?.(nodeId));
      }
      this.videos.set(key, video);
    }
    return v.readyState >= 2 ? v : undefined;
  }

  /** Sound was refused (no click yet): play silently rather than not at all. */
  private playMuted(v: HTMLVideoElement) {
    if (v.muted) return;
    v.muted = true;
    void v.play().catch(() => {});
  }

  /** The playing element of a video layer (for on-screen controls). */
  videoOf(id: string): HTMLVideoElement | undefined {
    for (const [v, layer] of this.layerOf) if (layer === id) return v;
    return undefined;
  }

  /** Apply a prototype's media action to a video layer. */
  apply(e: { id: string; playing: boolean; muted: boolean; seekTo?: number; seekBy?: number }) {
    for (const [v, id] of this.layerOf) {
      if (id !== e.id) continue;
      v.muted = e.muted;
      if (e.seekTo !== undefined) v.currentTime = Math.max(0, e.seekTo);
      if (e.seekBy !== undefined) v.currentTime = Math.max(0, Math.min(v.duration || Infinity, v.currentTime + e.seekBy));
      if (e.playing && v.paused) void v.play().catch(() => this.playMuted(v));
      else if (!e.playing && !v.paused) v.pause();
    }
  }

  private gif(src: string): CanvasImageSource | undefined {
    const g = this.gifs.get(src);
    if (g === 'loading' || g === 'still') return undefined;
    if (!g) {
      if (!ImageDecoderCtor) {
        this.gifs.set(src, 'still');
        return undefined;
      }
      this.gifs.set(src, 'loading');
      void this.decode(src).then(
        (d) => this.gifs.set(src, d),
        () => this.gifs.set(src, 'still'),
      );
      return undefined;
    }
    // the frame for "now", looping
    const t = (performance.now() - this.start) % Math.max(1, g.total);
    let acc = 0,
      i = 0;
    for (; i < g.frames.length - 1; i++) {
      acc += g.frames[i].ms;
      if (t < acc) break;
    }
    if (g.shown !== i) {
      const ctx = g.canvas.getContext('2d')!;
      ctx.clearRect(0, 0, g.canvas.width, g.canvas.height);
      ctx.drawImage(g.frames[i].frame, 0, 0);
      g.shown = i;
    }
    return g.canvas;
  }

  private async decode(src: string): Promise<Gif> {
    const data = await (await fetch(src)).arrayBuffer();
    const dec = new ImageDecoderCtor!({ data, type: 'image/gif' });
    await dec.tracks.ready;
    const count = Math.min(dec.tracks.selectedTrack?.frameCount ?? 1, 500);
    const frames: Gif['frames'] = [];
    for (let i = 0; i < count; i++) {
      const { image } = await dec.decode({ frameIndex: i });
      // GIF delays under 20ms play at 100ms, as browsers do
      const ms = image.duration ? image.duration / 1000 : 100;
      frames.push({ frame: image, ms: ms < 20 ? 100 : ms });
    }
    dec.close();
    const canvas = document.createElement('canvas');
    canvas.width = frames[0].frame.displayWidth;
    canvas.height = frames[0].frame.displayHeight;
    return { canvas, frames, total: frames.reduce((s, f) => s + f.ms, 0), shown: -1 };
  }

  dispose() {
    for (const v of this.videos.values()) {
      v.pause();
      v.removeAttribute('src');
      v.load();
    }
    for (const g of this.gifs.values()) if (typeof g === 'object') g.frames.forEach((f) => f.frame.close());
    this.videos.clear();
    this.layerOf.clear();
    this.gifs.clear();
  }
}
